import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppStoreType } from "@typings/global.types";

import { adsApi } from "../../api/ads";
import { charactersApi } from "../../api/characters";
import { exerciseEntrypointApi } from "../../api/exercise-entrypoint";
import { userInfoApi } from "../../api/user-info";

export const APP_SLICE_NAME = "APP";

const initialState: AppStoreType = {
  locale: "en-US",
  keyboardLayout: "qwerty",
  activeCharacterName: null,
  unAuthFirstString: "",
  speedRatio: 5,
  showResult: false,
  showKeyboard: false,
  showHands: true,
  diacriticActive: false,
  deadKey: "",
  errorKey: "",
  typeKeyboard: "default",
  metaKey: null,
  metaKeys: [16, 17, 18, 91],
  currentCoins: 0,
  progress: 0,
  isAuthorized: false,
  token: null,
  userData: {
    id: null,
    email: "",
    name: "",
    locale: "en-US",
    coins: 0,
    courseId: null,
    courseName: "",
    lessonId: null,
    previousExerciseId: null,
    lastExerciseId: null,
    lastLessonId: null,
    currentLessonId: null,
    currentExerciseId: null,
    layout: "",
    layoutLeftHand: "",
    layoutRightHand: "",
    lastExercise: null,
    lastLesson: null,
    currentExercise: null,
    nextLesson: null,
    regDatetime: "",
    gameMode: false,
    showKeyboard: false,
  },
  characters: [],
  isAdsScriptsInjected: false,
  isAdsScriptsReloaded: true,
  currentAdsPage: "",
  adsForceRefresh: false,
  adsData: {
    isLoaded: false,
    tags: {
      testString: "",
      testStringResult: "",
      result: "",
      mainBottom: "",
      mainRight: "",
      mainLeft: "",
      mainFullWidth: "",
      finishRight: "",
      finishLeft: "",
      finishBottom: "",
      challengeBottom: "",
    },
    scripts: {
      load: "",
      refresh: "",
    },
  },
};

export const appSlice = createSlice({
  name: APP_SLICE_NAME,
  initialState,
  reducers: {
    setLocale: (state, action: PayloadAction<AppStoreType["locale"]>) => {
      state.locale = action.payload;
    },
    setSpeedRatio: (state, action: PayloadAction<number>) => {
      state.speedRatio = action.payload;
    },
    setKeyboardLayout: (
      state,
      action: PayloadAction<AppStoreType["keyboardLayout"]>,
    ) => {
      state.keyboardLayout = action.payload;
    },
    setActiveCharacterName: (
      state,
      action: PayloadAction<AppStoreType["activeCharacterName"]>,
    ) => {
      state.activeCharacterName = action.payload;
    },
    setUnAuthFirstString: (
      state,
      action: PayloadAction<AppStoreType["unAuthFirstString"]>,
    ) => {
      state.unAuthFirstString = action.payload;
    },
    showResultUpload: (state, action: PayloadAction<boolean>) => {
      state.showResult = action.payload;
    },
    showKeyboardUpload: (state, action: PayloadAction<boolean>) => {
      state.showKeyboard = action.payload;
    },
    progressUpload: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    currentCoinsUpload: (state, action: PayloadAction<number>) => {
      state.currentCoins = action.payload;
    },
    metaKeyUpload: (state, action: PayloadAction<number | null>) => {
      state.metaKey = action.payload;
    },
    diacriticActiveUpload: (state, action: PayloadAction<boolean>) => {
      state.diacriticActive = action.payload;
    },
    typeKeyboardUpload: (state, action: PayloadAction<string>) => {
      state.typeKeyboard = action.payload;
    },
    deadKeyUpload: (state, action: PayloadAction<string>) => {
      state.deadKey = action.payload;
    },
    errorKeyUpload: (state, action: PayloadAction<string>) => {
      state.errorKey = action.payload;
    },
    showHandsUpload: (state, action: PayloadAction<boolean>) => {
      state.showHands = action.payload;
    },
    injectAdsScripts: (state) => {
      state.isAdsScriptsInjected = true;
    },
    setIsAdsScriptsReloaded: (state, action: PayloadAction<boolean>) => {
      state.isAdsScriptsReloaded = action.payload;
    },
    setCurrentAdsPage: (state, action: PayloadAction<string>) => {
      state.currentAdsPage = action.payload;
    },
    setAdsForceRefresh: (state, action: PayloadAction<boolean>) => {
      state.adsForceRefresh = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        userInfoApi.endpoints?.updateUserInfo.matchFulfilled,
        (state, { payload }) => {
          state.keyboardLayout = payload.layout;
        },
      )
      .addMatcher(
        exerciseEntrypointApi.endpoints?.getExerciseEntrypoint.matchFulfilled,
        (state, { payload }) => {
          state.showKeyboard = payload.user.showKeyboard;
          state.keyboardLayout = payload.user.layout;
          state.activeCharacterName = payload.character?.name ?? null;
        },
      )
      .addMatcher(
        adsApi.endpoints.getAds.matchFulfilled,
        (state, { payload }) => {
          state.adsData = payload;
        },
      )
      .addMatcher(
        charactersApi.endpoints.getCharacters.matchFulfilled,
        (state, { payload }) => {
          state.characters = payload.characters;

          if (state.userData.gameMode) {
            const activeCharacter = payload.characters.find(
              (item) => item.selected,
            )?.name;
            if (activeCharacter) state.activeCharacterName = activeCharacter;
          }
        },
      )
      .addMatcher(
        charactersApi.endpoints.updateCharacters.matchFulfilled,
        (state, { payload }) => {
          state.characters = payload.characters;
        },
      )
      .addMatcher(
        userInfoApi.endpoints.getUserInfo.matchFulfilled,
        (state, { payload }) => {
          // @ts-ignore
          state.userData = payload;
          // @ts-ignore
          state.showKeyboard = payload.showKeyboard;
        },
      )
      .addMatcher(
        userInfoApi.endpoints.updateUserInfo.matchFulfilled,
        (state, { payload }) => {
          // @ts-ignore
          state.userData = payload;
        },
      );
  },
});

export const {
  setLocale,
  setSpeedRatio,
  setKeyboardLayout,
  setActiveCharacterName,
  setUnAuthFirstString,
  showResultUpload,
  showKeyboardUpload,
  progressUpload,
  currentCoinsUpload,
  showHandsUpload,
  metaKeyUpload,
  diacriticActiveUpload,
  typeKeyboardUpload,
  deadKeyUpload,
  errorKeyUpload,
  injectAdsScripts,
  setIsAdsScriptsReloaded,
  setCurrentAdsPage,
  setAdsForceRefresh,
} = appSlice.actions;

export default appSlice.reducer;
